import { isAxiosError } from 'axios';
import stringify from 'json-stringify-safe';

const trimInterval = 30_000;
const ttl = 1000 * 60 * 60 * 4;
const version = 1;
const levels = ['trace', 'debug', 'info', 'warn', 'error'] as const;
type LogLevel = (typeof levels)[number];
let db: IDBDatabase | undefined;
let trimTimeoutId: ReturnType<typeof setTimeout> | undefined;
let logLevel: LogLevel = 'info';

const wrapTry =
  <A extends any[], R>(fn: (...args: A) => R, name = fn.name) =>
  (...args: A) => {
    try {
      return fn(...args);
    } catch (err) {
      console.error('logger fn failed', name, ...args, err);
    }
  };

const setLevel = (level: string) => {
  if (levels.includes(level as LogLevel)) {
    logLevel = level as LogLevel;
  }
};

const init = () => {
  if (db) {
    return;
  }

  const dbOpenRequest = window.indexedDB.open('logs', version);
  dbOpenRequest.onerror = (ev) => {
    console.error('logger db open error', ev);
  };

  dbOpenRequest.onsuccess = (ev) => {
    db = dbOpenRequest.result;
    console.log('logger db open success', db);

    db!.onclose = (ev) => {
      console.warn('logger db closed', ev);
      db = undefined;
    };
    db!.onerror = (ev) => {
      console.error('logger db error', ev);
      db = undefined;
    };

    scheduleTrim();
  };

  dbOpenRequest.onupgradeneeded = (ev) => {
    db = dbOpenRequest.result;

    const logsStore = db!.createObjectStore('logs', { autoIncrement: true });
    logsStore.createIndex('timestamp', 'timestamp', { unique: false });
  };
};

const readAllRecords = () =>
  new Promise<string[]>((resolve, reject) => {
    if (!db) {
      reject(new Error('logger db not available'));
      return;
    }

    const trx = db.transaction(['logs'], 'readonly');
    trx.onerror = (ev) => {
      console.error('logger read trx error', ev);
      reject(new Error('logger read trx error'));
    };

    const logsStore = trx.objectStore('logs');
    const req = logsStore.getAll();
    req.onerror = (ev) => {
      console.error('logger read req error', ev);
      reject(new Error('logger read req error'));
    };
    req.onsuccess = () => {
      resolve(req.result.map(({ record }) => record));
    };
  });

type UploadResult = { uploaded: boolean; count: number; key?: string };

const upload = async (): Promise<UploadResult> => {
  const records = await readAllRecords();

  if (records.length === 0) {
    return { uploaded: false, count: 0 };
  }

  const text = records.join('\n');
  const blob = new Blob([text], { type: 'application/x-ndjson' });
  const fileName = `${import.meta.env.VITE_BUILD_VERSION}.logs.v${version}.${new Date().toISOString().slice(0, 19)}.ndjson`;

  // Dynamic import because of circular dependency with swal dialog in the footer
  const { operatorLogsService } = await import('@/services/operatorLogs');
  const presignedUrlResponse = await operatorLogsService.generateOperatorLogsUploadPresignedUrl(fileName);

  if (!presignedUrlResponse?.url) {
    throw new Error('logger upload missing presigned url');
  }

  const response = await fetch(presignedUrlResponse.url, {
    method: 'PUT',
    body: blob,
    headers: {
      'Content-Type': 'application/x-ndjson',
    },
  });

  if (!response.ok) {
    throw new Error(`logger upload failed with status ${response.status}`);
  }

  return { uploaded: true, count: records.length, key: presignedUrlResponse.key };
};

// level/msg/timestamp should never be part of the outer context
type LogProps = { func: string; level?: never; msg?: never; timestamp?: never } & Record<string, any>;
export const log = {
  init: wrapTry(init),
  setLevel: wrapTry(setLevel),
  upload,
  ...levels.reduce(
    (log, level) => {
      // put level and message first so they are leftmost in logs for better readability
      log[level] = wrapTry((props, msg) => logRecord({ level, msg, ...props }), level);
      return log;
    },
    {} as Record<LogLevel, (props: LogProps, msg?: string) => void>,
  ),
};

type LogRecord = Omit<LogProps, 'level' | 'msg'> & { msg?: string; level: LogLevel };
const logRecord = (record: LogRecord) => {
  console[record.level]('logger logRecord', record);
  if (!db) {
    return console.warn('logger db not available');
  }
  if (levels.indexOf(record.level) < levels.indexOf(logLevel)) {
    return;
  }

  const trx = db.transaction(['logs'], 'readwrite');
  trx.onerror = (ev) => {
    console.error('logger trx error', ev);
    trimLogs();
  };

  const timestamp = new Date().toISOString();
  const logsStore = trx.objectStore('logs');
  // put timestamp first so it's leftmost in the logs for better readability
  const add = logsStore.add({ record: recordToString({ timestamp, ...record }), timestamp });
  add.onerror = (ev) => {
    console.error('logger add error', ev);
    trimLogs();
  };
};

const scheduleTrim = () => {
  clearTimeout(trimTimeoutId);
  trimTimeoutId = setTimeout(trimLogs, trimInterval);
};

const trimLogs = () => {
  if (!db) {
    return;
  }

  const cutoff = new Date(Date.now() - ttl);
  console.debug('logger trim cutoff', cutoff);

  const trx = db.transaction(['logs'], 'readwrite');
  trx.onerror = (ev) => {
    console.error('logger trim trx error', ev);
    scheduleTrim();
  };

  // req the cursor in desc order, get first record, delete all with <pk
  // cannot delete all with <cutoff because del all works only on pk
  const logsStore = trx.objectStore('logs');
  const tsIdx = logsStore.index('timestamp');
  const req = tsIdx.openCursor(IDBKeyRange.upperBound(cutoff.toISOString()), 'prev');
  req.onerror = (ev) => {
    console.error('logger trim cursor req error', ev);
    scheduleTrim();
  };
  req.onsuccess = (ev) => {
    const cursor = req.result;
    if (cursor) {
      const del = logsStore.delete(IDBKeyRange.upperBound(cursor.primaryKey));
      del.onerror = (ev) => {
        console.error('logger trim del req error', ev);
        scheduleTrim();
      };
      del.onsuccess = (ev) => {
        scheduleTrim();
      };
    } else {
      scheduleTrim();
    }
  };
};

const recordToString = (record: LogRecord) => {
  const { error, err, ...rest } = record;
  return stringify({
    ...rest,
    error: error && errorToJson(error),
    err: err && errorToJson(err),
  });
};

const errorToJson = (err: any, seen: WeakSet<any> = new WeakSet()): any => {
  if (!(err instanceof Object)) {
    return err;
  }

  if (seen.has(err)) {
    return '[Circular]';
  } else {
    seen.add(err);
  }

  if (isAxiosError(err)) {
    return {
      name: err.name,
      message: err.message,
      status: err.response?.status,
      code: err.code,
      stack: err.stack,
    };
  }

  if (!(err instanceof Error)) {
    return err;
  }

  const { cause, message, stack, name, ...rest } = err;
  return { cause: errorToJson(err, seen), message, stack, name, ...rest };
};
